import { useEffect, useState, useRef } from 'react'
import { ArrowLeft, Send, User, Image as ImageIcon, Loader2, Share2, Check, X, Reply, Pencil, Trash2, MoreVertical } from 'lucide-react'
import { Conversation, Message } from '@/types'
import { api } from '@/utils/api'
import { formatChatTime, formatChatDate } from '@/utils/datetime'
import { useChat } from '@/hooks/useChat'
import { socket } from '@/utils/socket'
import AlertModal from './ui/AlertModal'
import ConfirmModal from './ui/ConfirmModal'

interface ChatWindowProps {
  conversation: Conversation
  onBack: () => void
}

export default function ChatWindow({ conversation, onBack }: ChatWindowProps) {
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [justShared, setJustShared] = useState(false)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [editingMessage, setEditingMessage] = useState<Message | null>(null)
  const [showMenuId, setShowMenuId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  
  // Modal states
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })
  const [confirmConfig, setConfirmConfig] = useState<{ isOpen: boolean; title: string; message: string; onConfirm: () => void; isDestructive?: boolean }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isDestructive: false
  })
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { messages, setMessages, addMessage } = useChat()
  const listRef = useRef<HTMLDivElement>(null)
  const lastFetchedAtRef = useRef<string | null>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenuId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    let isSubscribed = true
    
    const loadMessages = async () => {
      try {
        setIsLoading(true)
        const data = await api.getMessages(conversation.id)
        if (!isSubscribed) return
        setMessages(data)
        if (data.length > 0) {
          lastFetchedAtRef.current = data[data.length - 1].createdAt
          setTimeout(() => scrollToBottom('auto'), 50)
        }
      } catch (error) {
        console.error('Failed to load messages:', error)
      } finally {
        if (isSubscribed) {
          setIsLoading(false)
        }
      }
    }

    loadMessages()

    // Socket.IO Integration
    const setupSocket = () => {
      if (!socket.connected) {
        socket.connect()
      }
      
      socket.emit('join', `conversation:${conversation.id}`)
    }

    setupSocket()
    
    socket.on('connect', setupSocket)

    const handleNewMessage = (message: Message) => {
      if (!isSubscribed) return
      if (message.conversationId === conversation.id) {
        const currentMessages = useChat.getState().messages
        
        // Check if this message is already in our state (either by ID or by matching content/sender for temp messages)
        const existingMessageIndex = currentMessages.findIndex(m => 
          m.id === message.id || 
          (m.id.startsWith('temp-') && m.content === message.content && m.sender === message.sender)
        )

        if (existingMessageIndex === -1) {
          addMessage(message)
          setTimeout(() => scrollToBottom('smooth'), 50)
        } else {
          // If it's a temp message matching the real one, update it in place
          const updatedMessages = [...currentMessages]
          updatedMessages[existingMessageIndex] = { 
            ...message, 
            status: 'sent' 
          }
          setMessages(updatedMessages)
        }
      }
    }

    const handleMessageEdited = (message: Message) => {
      if (!isSubscribed) return
      setMessages(useChat.getState().messages.map(m => 
        m.id === message.id ? { ...m, content: message.content, edited: true, editedAt: message.editedAt } : m
      ))
    }

    const handleMessageDeleted = (messageId: string) => {
      if (!isSubscribed) return
      setMessages(useChat.getState().messages.map(m => 
        m.id === messageId ? { ...m, content: 'This message was deleted', deleted: true, deletedAt: new Date().toISOString() } : m
      ))
    }

    socket.on('new_message', handleNewMessage)
    socket.on('message_edited', handleMessageEdited)
    socket.on('message_deleted', handleMessageDeleted)
    
    return () => {
      isSubscribed = false
      socket.emit('leave', `conversation:${conversation.id}`)
      socket.off('connect', setupSocket)
      socket.off('new_message', handleNewMessage)
      socket.off('message_edited', handleMessageEdited)
      socket.off('message_deleted', handleMessageDeleted)
    }
  }, [conversation.id, addMessage, setMessages])

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim()) return

    const content = newMessage.trim()
    const tempId = `temp-${Date.now()}`
    
    // If editing, handle that separately
    if (editingMessage) {
      handleEditMessage(editingMessage.id, content)
      return
    }

    const optimisticMessage: Message = {
      id: tempId,
      conversationId: conversation.id,
      sender: 'ADMIN',
      senderName: conversation.currentPersonaName || 'Admin',
      content: content,
      createdAt: new Date().toISOString(),
      status: 'sending',
      replyTo: replyingTo ? {
        id: replyingTo.id,
        content: replyingTo.content,
        sender: replyingTo.sender === 'ADMIN' ? (replyingTo.senderName || 'Admin') : (conversation.userName || 'User')
      } : null
    }

    addMessage(optimisticMessage)
    setNewMessage('')
    setReplyingTo(null)
    setTimeout(() => scrollToBottom('smooth'), 50)

    // Call API for persistence
    try {
      const savedMessage = await api.sendMessage({
        conversationId: conversation.id,
        content: content,
        replyToId: replyingTo?.id
      })
      
      // Update optimistic message with real data from server
      setMessages(useChat.getState().messages.map(m => 
        m.id === tempId ? { ...savedMessage, status: 'sent' } : m
      ))
    } catch (error) {
      console.error('Failed to persist message:', error)
      // Mark as failed
      setMessages(useChat.getState().messages.map(m => 
        m.id === tempId ? { ...m, status: 'failed' } : m
      ))
    }
  }

  const handleEditMessage = async (messageId: string, newContent: string) => {
    const originalMessages = [...useChat.getState().messages]
    
    // Optimistic update
    setMessages(originalMessages.map(m => 
      m.id === messageId ? { ...m, content: newContent, edited: true, editedAt: new Date().toISOString() } : m
    ))
    setEditingMessage(null)
    setNewMessage('')

    // Emit via socket
    socket.emit('edit_message', { messageId, content: newContent, room: conversation.id })

    try {
      await api.editMessage({ messageId, content: newContent })
    } catch (error) {
      console.error('Failed to edit message:', error)
      setMessages(originalMessages) // Rollback
      setAlertConfig({
        isOpen: true,
        title: 'Error',
        message: 'Failed to edit message. Please try again.',
        type: 'error'
      })
    }
  }

  const handleDeleteMessage = async (messageId: string) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Message',
      message: 'Are you sure you want to delete this message? This action cannot be undone.',
      isDestructive: true,
      onConfirm: async () => {
        const originalMessages = [...useChat.getState().messages]
        
        // Optimistic update (soft delete)
        setMessages(originalMessages.map(m => 
          m.id === messageId ? { ...m, content: 'This message was deleted', deleted: true, deletedAt: new Date().toISOString() } : m
        ))
        setShowMenuId(null)

        try {
          // Emit via socket
          socket.emit('delete_message', { messageId, conversationId: conversation.id, mode: 'everyone' })
          
          await api.deleteMessage(messageId)
        } catch (error) {
          console.error('Failed to delete message:', error)
          setMessages(originalMessages) // Rollback
          setAlertConfig({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete message. Please try again.',
            type: 'error'
          })
        }
      }
    })
  }


  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file.')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB.')
      return
    }

    setIsUploading(true)
    const formData = new FormData()
    formData.append('image', file)
    formData.append('conversationId', conversation.id)
    if (newMessage.trim()) {
      formData.append('content', newMessage.trim())
    }
    if (replyingTo?.id) {
      formData.append('replyToId', replyingTo.id)
    }

    try {
      const message = await api.sendImageMessage(formData)
      
      // Check if message was already added by socket
      const currentMessages = useChat.getState().messages
      if (!currentMessages.find(m => m.id === message.id)) {
        addMessage(message)
        setTimeout(() => scrollToBottom('smooth'), 100)
      }
      
      setNewMessage('')
      setReplyingTo(null)
    } catch (error) {
      console.error('Failed to upload image:', error)
      alert('Failed to upload image.')
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleShare = async () => {
    setIsSharing(true)
    try {
      const { shareId } = await api.shareConversation(conversation.id)
      const shareUrl = `${window.location.origin}/shared/${shareId}`
      
      // Always try to copy to clipboard first as a fallback
      try {
        await navigator.clipboard.writeText(shareUrl)
        setJustShared(true)
        setTimeout(() => setJustShared(false), 3000)
      } catch (err) {
        console.warn('Clipboard copy failed:', err)
      }

      // Then try system share if available
      if (navigator.share) {
        try {
          await navigator.share({
            title: `Support Chat with ${conversation.userName || 'User'}`,
            text: `View the chat history for ${conversation.userName || 'User'}`,
            url: shareUrl,
          })
        } catch (shareErr) {
          // Ignore AbortError (user cancelled)
          if ((shareErr as any).name !== 'AbortError') {
            console.error('System share failed:', shareErr)
          }
        }
      }
    } catch (error) {
      console.error('Failed to generate share link:', error)
      alert('Failed to generate share link')
    } finally {
      setIsSharing(false)
    }
  }

  const scrollToMessage = (messageId: string) => {
    const el = document.getElementById(`msg-${messageId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('bg-primary-50')
      setTimeout(() => el.classList.remove('bg-primary-50'), 2000)
    }
  }

  return (
    <div className="flex-1 flex flex-col bg-white min-h-0 relative">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center space-x-2 md:space-x-4 p-2 md:p-4 border-b border-gray-200 bg-white">
        <button
          onClick={onBack}
          className="lg:hidden p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <div className="flex-1 flex items-center justify-between min-w-0">
          <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
            <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm md:text-base font-bold text-gray-900 truncate">
                {conversation.userName || 'User'}
              </h2>
              <p className="text-[10px] md:text-xs text-green-500 font-bold uppercase tracking-widest">
                Active
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 md:space-x-4">
            {conversation.currentPersonaName && (
              <div className="hidden sm:flex items-center space-x-2 bg-primary-50 px-3 py-1.5 rounded-full border border-primary-100">
                <div className="h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
                <span className="text-xs font-medium text-primary-700">
                  Replying as: <span className="font-bold">{conversation.currentPersonaName}</span>
                </span>
              </div>
            )}

            <button
              onClick={handleShare}
              disabled={isSharing}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                justShared 
                  ? 'bg-green-50 text-green-600 border border-green-200' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              title="Export/Share Chat"
            >
              {isSharing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : justShared ? (
                <Check className="h-4 w-4" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              <span>
                {justShared ? 'Link Copied!' : 'Export Chat'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4" ref={listRef}>
        {isLoading ? (
          <div className="text-center text-gray-500 py-8">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">No messages yet</div>
        ) : (
          (Array.isArray(messages) ? messages : []).map((message, idx) => {
            const isSystem = message.sender === 'ADMIN' && !message.senderName;
            const prev = messages[idx - 1]
            const showDate = !prev || formatChatDate(prev.createdAt) !== formatChatDate(message.createdAt)
            
            if (isSystem) {
              return (
                <div key={message.id} id={`msg-${message.id}`}>
                  {showDate && (
                    <div className="flex justify-center py-2"><span className="text-xs text-gray-500">{formatChatDate(message.createdAt)}</span></div>
                  )}
                  <div className="flex justify-center py-2">
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full border border-gray-200 shadow-sm">
                      {message.content}
                    </span>
                  </div>
                </div>
              );
            }

            const isAdmin = message.sender === 'ADMIN';

            return (
              <div key={message.id} id={`msg-${message.id}`} className="transition-colors duration-500 rounded-lg">
                {showDate && (
                  <div className="flex justify-center py-2"><span className="text-xs text-gray-500">{formatChatDate(message.createdAt)}</span></div>
                )}
                <div className={`flex items-start ${isAdmin ? 'justify-end' : 'justify-start'} group`}>
                  <div className={`flex items-start max-w-[90%] sm:max-w-[80%] ${isAdmin ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className={`px-3 md:px-4 py-2 md:py-2.5 rounded-lg shadow-sm ${isAdmin ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                      
                      {/* Reply Reference */}
                      {message.replyTo && (
                        <div 
                          onClick={() => scrollToMessage(message.replyTo!.id)}
                          className={`mb-2 p-2 rounded border-l-4 cursor-pointer text-xs ${isAdmin ? 'bg-primary-700 border-primary-300 text-primary-100' : 'bg-gray-200 border-gray-400 text-gray-600'}`}
                        >
                          <p className="font-bold mb-0.5">
                            {message.replyTo.sender === 'ADMIN' ? 'Support' : (conversation.userName || 'User')}
                          </p>
                          <p className="line-clamp-2 italic">{message.replyTo.content}</p>
                        </div>
                      )}

                      {message.imageUrl && !message.deleted && (
                        <div className="mb-2">
                          <img 
                            src={message.imageUrl.startsWith('http') ? message.imageUrl : `${(import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '')}${message.imageUrl}`}
                            alt="Attachment" 
                            className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90"
                            onClick={() => window.open(message.imageUrl?.startsWith('http') ? message.imageUrl : `${(import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '')}${message.imageUrl}`, '_blank')}
                          />
                        </div>
                      )}
                      
                      <p className={`text-sm leading-snug md:leading-normal ${message.deleted ? 'italic opacity-70' : ''}`}>
                        {message.content}
                        {message.edited && !message.deleted && (
                          <span className="ml-1 text-[10px] opacity-70">(edited)</span>
                        )}
                      </p>

                        <div className="absolute bottom-1 right-2 flex items-center gap-1">
                          {message.edited && !message.deleted && (
                            <span className="text-[10px] opacity-70">(edited)</span>
                          )}
                          <p className={`text-[10px] ${isAdmin ? 'text-primary-100' : 'text-gray-500'}`}>
                            {formatChatTime(message.createdAt)}
                          </p>
                          {isAdmin && message.status === 'sending' && (
                            <Loader2 className="h-2 w-2 animate-spin text-primary-200" />
                          )}
                          {isAdmin && message.status === 'failed' && (
                            <X className="h-2 w-2 text-red-300" />
                          )}
                        </div>
                    </div>

                    {/* Actions Menu (Always part of flex flow to ensure visibility) */}
                    {!message.deleted && (
                      <div className={`flex-shrink-0 self-center ${isAdmin ? 'mr-1' : 'ml-1'}`}>
                        <div className="relative" ref={showMenuId === message.id ? menuRef : null}>
                          <button 
                            onClick={() => setShowMenuId(showMenuId === message.id ? null : message.id)}
                            className={`p-1 rounded-full transition-all ${
                              showMenuId === message.id 
                                ? 'bg-gray-200 text-gray-700' 
                                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                            }`}
                            title="Message Actions"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>

                          {showMenuId === message.id && (
                            <div className={`absolute z-20 bottom-full mb-2 ${isAdmin ? 'right-0' : 'left-0'} bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[140px]`}>
                              <button 
                                onClick={() => {
                                  setReplyingTo(message)
                                  setShowMenuId(null)
                                }}
                                className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Reply className="h-4 w-4 text-gray-500" />
                                <span className="font-medium">Reply</span>
                              </button>
                              {isAdmin && (
                                <>
                                  <button 
                                    onClick={() => {
                                      setEditingMessage(message)
                                      setNewMessage(message.content)
                                      setShowMenuId(null)
                                    }}
                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                  >
                                    <Pencil className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium">Edit</span>
                                  </button>
                                  <div className="h-px bg-gray-100 my-1" />
                                  <button 
                                    onClick={() => {
                                      handleDeleteMessage(message.id)
                                      setShowMenuId(null)
                                    }}
                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    <span className="font-medium">Delete</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply/Edit Preview */}
      {(replyingTo || editingMessage) && (
        <div className="flex-shrink-0 px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="p-1 bg-primary-100 rounded text-primary-600">
              {editingMessage ? <Pencil className="h-4 w-4" /> : <Reply className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-primary-700">
                {editingMessage ? 'Editing message' : `Replying to ${replyingTo?.sender === 'ADMIN' ? (replyingTo?.senderName || 'Admin') : (conversation.userName || 'User')}`}
              </p>
              <p className="text-xs text-gray-500 truncate italic">
                {editingMessage ? editingMessage.content : replyingTo?.content}
              </p>
            </div>
          </div>
          <button 
            onClick={() => {
              setReplyingTo(null)
              setEditingMessage(null)
              if (editingMessage) setNewMessage('')
            }}
            className="p-1 hover:bg-gray-200 rounded-full text-gray-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="flex-shrink-0 p-3 md:p-4 border-t border-gray-200 bg-white">
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleImageUpload} 
          className="hidden" 
          accept="image/*"
        />
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isLoading}
            className="h-12 w-12 md:h-10 md:w-10 text-gray-500 hover:text-primary-600 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {isUploading ? (
              <Loader2 className="h-5 w-5 md:h-4 md:w-4 animate-spin" />
            ) : (
              <ImageIcon className="h-5 w-5 md:h-4 md:w-4" />
            )}
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={editingMessage ? "Edit message..." : "Type a message..."}
            className="flex-1 px-3 py-3 md:py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={isLoading || isUploading}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isLoading || isUploading}
            className="h-12 w-12 md:h-10 md:w-10 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            <Send className="h-5 w-5 md:h-4 md:w-4" />
          </button>
        </div>
      </form>

      {/* Dropdown Menu Portal Area (optional, but keep it inside relative parent if needed) */}

      {/* Modals */}
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        isDestructive={confirmConfig.isDestructive}
      />
    </div>
  )
}
